import React from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight } from 'react-icons/fa';

const Industries = () => {
    const cards = [
        // Row 1
        {
            id: 'finance',
            category: 'INDUSTRY SECTOR',
            title: 'Reinventing Finance with DeFi & Blockchain',
            industry: 'Banking & Finance',
            theme: 'bg-white text-black',
            borderColor: 'border-transparent',
            // NEW RELIABLE IMAGE: Coins/Graph
            image: "https://images.unsplash.com/photo-1605792657660-596af9009e82?q=80&w=2002&auto=format&fit=crop",
            imageStyle: "grayscale opacity-80 mix-blend-multiply"
        },
        {
            id: 'health',
            category: 'CASE STUDY',
            title: 'AI-First Approach to Patient Care',
            industry: 'Healthcare',
            theme: 'bg-[#1a0b2e] text-white',
            borderColor: 'border-purple-500/30',
            image: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?q=80&w=2670&auto=format&fit=crop",
            imageStyle: "opacity-60 mix-blend-screen hue-rotate-180"
        },
        {
            id: 'tech',
            category: 'RESEARCH REPORT',
            title: 'The Future of Agentic AI Platforms',
            industry: 'Technology',
            theme: 'bg-[#1e3a8a] text-white',
            borderColor: 'border-blue-800',
            image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=2000&auto=format&fit=crop", // Robot/AI
            imageStyle: "opacity-60 mix-blend-overlay"
        },
        {
            id: 'retail',
            category: 'TRENDS 2026',
            title: 'Hyper-Personalized Retail Engines',
            industry: 'Retail',
            theme: 'bg-[#240046] text-white',
            borderColor: 'border-fuchsia-900',
            image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?q=80&w=2000&auto=format&fit=crop", // Shopping
            imageStyle: "opacity-60 mix-blend-overlay"
        },
        // Row 2
        {
            id: 'manufacturing',
            category: 'INDUSTRY 4.0',
            title: 'Smart Factories & IoT Automation',
            industry: 'Manufacturing',
            theme: 'bg-[#0f172a] text-white',
            borderColor: 'border-blue-900',
            image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2670&auto=format&fit=crop",
            imageStyle: "opacity-60 mix-blend-overlay"
        },
        {
            id: 'energy',
            category: 'SUSTAINABILITY',
            title: 'Green Tech & Renewable Grids',
            industry: 'Energy',
            theme: 'bg-[#064e3b] text-white',
            borderColor: 'border-emerald-800',
            image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?q=80&w=2670&auto=format&fit=crop",
            imageStyle: "opacity-50 mix-blend-hard-light"
        },
        {
            id: 'education',
            category: 'DIGITAL LEARNING',
            title: 'Immersive VR Classrooms Global',
            industry: 'Education',
            theme: 'bg-[#4338ca] text-white',
            borderColor: 'border-indigo-500',
            image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?q=80&w=2604&auto=format&fit=crop",
            imageStyle: "opacity-60 mix-blend-overlay"
        },
        {
            id: 'media',
            category: 'MEDIA & COMM',
            title: 'Next-Gen Streaming Architecture',
            industry: 'Media',
            theme: 'bg-[#450a0a] text-white', // Dark Red
            borderColor: 'border-red-900',
            // NEW RELIABLE IMAGE: Abstract Abstract Lights/Streaming
            image: "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?q=80&w=2000&auto=format&fit=crop",
            imageStyle: "opacity-60 mix-blend-screen"
        }
    ];

    return (
        <section className="py-8 md:py-16 bg-slate-50 text-slate-900 relative overflow-hidden">

            <div className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">

                <div className="flex flex-col md:flex-row justify-between items-end mb-6 border-b border-slate-200 pb-4">
                    <div className="w-full">
                        <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-2 text-slate-900">
                            Industries
                        </h2>
                        <p className="text-sm md:text-lg text-slate-500 w-full max-w-3xl leading-relaxed">
                            Deep expertise across vectors. Delivering value at the speed of innovation.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {cards.map((card, index) => (
                        <motion.div
                            key={card.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05, duration: 0.5 }}
                            className={`group relative h-[380px] flex flex-col justify-between p-6 overflow-hidden transition-transform duration-500 hover:-translate-y-1 ${card.theme} ${card.borderColor ? `border ${card.borderColor}` : ''}`}
                        >
                            <div className="relative z-20">
                                <span className={`block text-[10px] font-bold tracking-widest uppercase mb-3 ${card.theme.includes('bg-white') ? 'text-gray-600' : 'text-gray-400'}`}>
                                    {card.category}
                                </span>
                                <h3 className={`text-lg font-bold leading-tight line-clamp-2 ${card.theme.includes('bg-white') ? 'text-black' : 'text-white'}`}>
                                    {card.title}
                                </h3>
                                <div className={`mt-3 inline-block px-2 py-0.5 text-[9px] font-bold uppercase border ${card.theme.includes('bg-white') ? 'border-gray-300 text-gray-800' : 'border-white/30 text-white/80'}`}>
                                    {card.industry}
                                </div>
                            </div>

                            <div className="relative z-10 mt-auto pt-6">
                                <div className={`w-full flex items-center justify-between border-t ${card.theme.includes('bg-white') ? 'border-gray-200' : 'border-white/10'} pt-3`}>
                                    <span className={`text-[10px] font-bold uppercase tracking-widest ${card.theme.includes('bg-white') ? 'text-gray-800' : 'text-white/80'}`}>Read Case Study</span>
                                    <FaArrowRight className={`text-xs transform group-hover:translate-x-1 transition-transform ${card.theme.includes('bg-white') ? 'text-black' : 'text-white'}`} />
                                </div>
                            </div>

                            <div className="absolute inset-x-0 bottom-0 h-1/2 z-0 pointer-events-none">
                                <img
                                    src={card.image}
                                    alt={card.industry}
                                    className={`w-full h-full object-cover object-bottom transition-transform duration-700 group-hover:scale-105 ${card.imageStyle}`}
                                />
                                <div className={`absolute inset-0 bg-gradient-to-b ${card.theme.includes('bg-white')
                                    ? 'from-white via-white/50 to-transparent'
                                    : card.theme.includes('bg-black')
                                        ? 'from-black via-black/50 to-transparent'
                                        : 'from-transparent to-transparent'
                                    }`}></div>
                            </div>
                        </motion.div>
                    ))}
                </div>

            </div>
        </section>
    );
};

export default Industries;
